/**
 * QuestionCardWrapper - V2 styled wrapper for question type components
 * Wraps existing question components with modern styling and adds answer reveal feature
 */
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import parse from 'html-react-parser';
import { FiCheck, FiX, FiFileText } from 'react-icons/fi';
import { Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import httpService from 'services/httpService';
import * as constants from 'Constants';
import displayGlobalMessage from 'Utils/DisplayGlobalMessage';
import DocumentView from 'Screens1/Student/Course/Content/Multimedia/Components/DocumentView/DocumentView';
import log from 'loglevel';
import styles from './StudentSubmissionV2.module.css';

const toRelativeFilePath = (rawUrl) => {
    if (!rawUrl) return '';
    try {
        const parsed = new URL(rawUrl, window.location.origin);
        return `${parsed.pathname}${parsed.search}`;
    } catch (err) {
        return rawUrl;
    }
};

const QuestionCardWrapper = ({
  question,
  index,
  courseId,
  labId,
  activityData,
  updateResponse,
  shouldShowMarks,
  isInstructor,
  studentEmail,
  isPreviewMode = false,
}) => {
  const navigate = useNavigate();
  const [showAnswer, setShowAnswer] = useState(false);
  const [isGrading, setIsGrading] = useState(false);
  const [gradingMarks, setGradingMarks] = useState('');
  const [gradingComments, setGradingComments] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  const { activityID, courseId: paramCourseId, labId: paramLabId } = useParams();
  const userInfo = useSelector((state) => state?.userInfo);

  const {
    question_type,
    marks,
    negative_marks,
    question_text,
    questionoption_set,
    questionanswer_set,
    questionresponse_set,
    answer_explaination,
  } = question;

  const previousResponse = questionresponse_set || {};
  const isDisabled = true; // View-only mode for submissions
  const isEvaluated = activityData?.evaluated || false;

  // Get question type display name
  const getQuestionTypeName = () => {
    switch (question_type) {
      case 'S':
      case 'SC':
      case 'ST':
        return 'Single Correct';
      case 'M':
        return 'Multiple Correct';
      case 'D':
      case 'PA':
        return 'Subjective';
      case 'T':
        return 'Text';
      case 'F':
      case 'FB':
        return 'Fill Blank(s)';
      case 'N':
        return 'Numeric';
      case 'U':
        return 'File Upload';
      default:
        return question_type;
    }
  };

  // Remove remarks from question text (same as old GradeCheck.jsx)
  const removeRemarks = (text) => {
    if (!text) return '';

    // Find the index of "REMARKS=" within the question text
    const remarksIndex = text.indexOf("REMARKS=");
    if (remarksIndex !== -1) {
      // If "REMARKS=" is found, remove everything after it
      text = text.slice(0, remarksIndex);
    }

    // Remove any styling related to "REMARKS="
    text = text.replace(/style=".*?"/g, '');
    // Update font size
    text = text.replace(/font\s+size\s*=\s*"\d+"/g, 'font size="20px"');

    // Also remove HTML comment remarks
    text = text.replace(/<!--.*?-->/gs, '');

    return text;
  };

  // Get correct answer text for different question types
  const getCorrectAnswerText = () => {
    if (!questionanswer_set || questionanswer_set.length === 0) return null;

    switch (question_type) {
      case 'S':
      case 'SC':
      case 'ST':
      case 'M':
        // For choice questions, get the correct option texts
        const correctOptionIds = questionanswer_set.map(ans => ans.answer_option);
        const correctOptions = questionoption_set?.filter(opt => correctOptionIds.includes(opt.id));
        if (correctOptions && correctOptions.length > 0) {
          return correctOptions.map(opt => `"${stripPrefix(opt.text || opt.option_text || '')}"`).join(', ');
        }
        return null;
      case 'F':
      case 'FB':
      case 'N':
        // For fill blanks and numeric, show the answer field
        return questionanswer_set.map(ans => `"${ans.answer}"`).join(', ');
      default:
        return null;
    }
  };

  // Get correct answer option IDs for choice questions
  const getCorrectOptionIds = () => {
    if (!questionanswer_set) return [];
    return questionanswer_set.map(ans => ans.answer_option);
  };

  // Check if an option is correct
  const isOptionCorrect = (optionId) => {
    const correctIds = getCorrectOptionIds();
    return correctIds.includes(optionId);
  };

  // Check if an option was selected by the student
  const isOptionSelected = (optionId) => {
    return previousResponse?.response_options_ids?.includes(optionId) || false;
  };

  // Helper function to strip letter prefixes like "A. ", "B. ", etc.
  // Same as old code in Options/index.jsx
  const stripPrefix = (text) => {
    if (!text) return '';
    return String(text).replace(/^[A-Z]\.\s*/, '');
  };

  // Render choice question options with V2 styling
  const renderChoiceOptions = () => {
    if (!questionoption_set) return null;

    return (
      <div className={styles.optionsWrapper}>
        {questionoption_set.map((option, idx) => {
          const selected = isOptionSelected(option.id);
          const correct = isOptionCorrect(option.id);
          const showCorrectness = showAnswer;

          let optionClass = styles.optionItem;
          if (selected) {
            optionClass += ` ${styles.selected}`;
            if (showCorrectness) {
              optionClass += correct ? ` ${styles.correct}` : ` ${styles.incorrect}`;
            }
          } else if (showCorrectness && correct) {
            optionClass += ` ${styles.correct}`;
          }

          // Option text field is 'text' (same as old code: const { id, text } = option)
          const optionText = option.text ?? option.option_text ?? '';

          return (
            <div key={option.id} className={optionClass}>
              <span className={`${styles.optionRadio} ${selected ? styles.selected : ''}`}></span>
              <span className={styles.optionText}>
                {stripPrefix(optionText)}
              </span>
              {showCorrectness && correct && (
                <span className={styles.correctLabel}>
                  <FiCheck size={14} /> Correct
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const getFileUrl = (url) => url || null;

  // Helper to determine if URL points to an image file
  const isImageFile = (url) => {
    if (!url) return false;
    const lowerUrl = url.toLowerCase();
    return (
      lowerUrl.endsWith('.jpg') ||
      lowerUrl.endsWith('.jpeg') ||
      lowerUrl.endsWith('.png') ||
      lowerUrl.endsWith('.gif') ||
      lowerUrl.endsWith('.webp')
    );
  };

  // Render subjective response
  const renderSubjectiveResponse = () => {
    const responseText = previousResponse?.response_text;
    const responseFile = previousResponse?.response_file;

    if (question_type === 'U') {
      // File upload
      const fileUrl = getFileUrl(responseFile) || getFileUrl(responseText);
      if (fileUrl) {
        const fileName = fileUrl.split('/').pop();
        const openPreview = () => setPreviewFile({ url: fileUrl, name: fileName });
        return (
          <div className={`${styles.subjectiveResponse} ${styles.hasContent}`}>
            <div className={styles.fileUploadPreview}>
              {isImageFile(fileUrl) ? (
                <img
                  src={fileUrl}
                  alt="Uploaded file"
                  onClick={openPreview}
                  style={{ cursor: 'pointer' }}
                />
              ) : (
                <button
                  type="button"
                  className={styles.filePreviewBtn}
                  onClick={openPreview}
                  title={fileName}
                >
                  <FiFileText size={18} />
                  <span className={styles.filePreviewLabel}>View submitted file</span>
                  <span className={styles.filePreviewName}>{fileName}</span>
                </button>
              )}
            </div>
          </div>
        );
      }
      return (
        <div className={styles.subjectiveResponse}>
          <p className={styles.placeholderText}>
            {isPreviewMode ? '[Student response area]' : '[No response submitted]'}
          </p>
        </div>
      );
    }

    // Text-based subjective
    if (responseText) {
      return (
        <div className={`${styles.subjectiveResponse} ${styles.hasContent}`}>
          {parse(responseText)}
        </div>
      );
    }

    return (
      <div className={styles.subjectiveResponse}>
        <p className={styles.placeholderText}>
          {isPreviewMode ? '[Student response area]' : '[No response submitted]'}
        </p>
      </div>
    );
  };

  // Render fill blanks response
  const renderFillBlanksResponse = () => {
    const responseText = previousResponse?.response_text;

    if (responseText) {
      const lines = responseText.split(/\r?\n/);
      return (
        <div className={styles.fillBlanksResponse}>
          {lines.map((line, idx) => (
            <div key={idx}>{line || '(empty)'}</div>
          ))}
        </div>
      );
    }

    return (
      <div className={styles.subjectiveResponse}>
        <p className={styles.placeholderText}>
          {isPreviewMode ? '[Student response area]' : '[No response submitted]'}
        </p>
      </div>
    );
  };

  // Render numeric response
  const renderNumericResponse = () => {
    const responseText = previousResponse?.response_text;

    if (responseText) {
      return (
        <div>
          <span className={styles.numericResponse}>{responseText}</span>
        </div>
      );
    }

    return (
      <div className={styles.subjectiveResponse}>
        <p className={styles.placeholderText}>
          {isPreviewMode ? '[Student response area]' : '[No response submitted]'}
        </p>
      </div>
    );
  };

  // Render question content based on type
  const renderQuestionContent = () => {
    switch (question_type) {
      case 'S':
      case 'SC':
      case 'ST':
      case 'M':
        return renderChoiceOptions();
      case 'D':
      case 'PA':
      case 'T':
      case 'U':
        return renderSubjectiveResponse();
      case 'F':
      case 'FB':
        return renderFillBlanksResponse();
      case 'N':
        return renderNumericResponse();
      default:
        return <div>Unknown question type: {question_type}</div>;
    }
  };

  // Check if question needs grading button (subjective types)
  const needsGradeButton = () => {
    return question_type === 'D' || question_type === 'PA' || question_type === 'T' || question_type === 'U';
  };

  // Handle opening grading form - navigates to subjective grading workspace
  const handleOpenGrading = () => {
    const targetActivityId = activityID || activityData?.id;
    const targetCourseId = courseId || paramCourseId;
    const targetLabId = labId || paramLabId;
    const encodedStudent = encodeURIComponent(studentEmail || '');
    navigate(
      `/courseware/course/${targetCourseId}/content/labs/${targetLabId}/activities/other/${targetActivityId}/submissions/${encodedStudent}/grade/${question?.id}`
    );
  };

  // Handle closing grading form
  const handleCloseGrading = () => {
    setIsGrading(false);
    setGradingMarks('');
    setGradingComments('');
  };

  // Handle saving grading
  const handleGradeSave = async () => {
    const marksValue = parseFloat(gradingMarks);

    // Validate marks
    if (isNaN(marksValue) || marksValue < 0) {
      displayGlobalMessage('Please enter a valid marks value', 'error');
      return;
    }

    if (marksValue > marks) {
      displayGlobalMessage(`Question Grading failed: Marks cannot exceed ${marks}`, 'error');
      return;
    }

    setIsSaving(true);
    try {
      const user = userInfo?.name || userInfo?.username;
      const gradeData = {
        marks: marksValue,
        comments: gradingComments,
        graded_by: user,
        question: question?.id,
        student_id: studentEmail,
        otherlab: question?.otherlab,
        graded: true,
      };

      const url = `${constants.base_url}/lab/activity/${activityID}?type=other_type_grade_submission`;
      const response = await httpService.put(url, gradeData);

      if (response.status === 200) {
        displayGlobalMessage('Question Graded Successfully', 'success');
        setIsGrading(false);
        // Refresh submission data
        if (updateResponse) {
          updateResponse();
        }
      } else {
        displayGlobalMessage('Question Grading failed', 'error');
      }
    } catch (error) {
      log.error('Error grading question:', error);
      displayGlobalMessage('Question Grading failed', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const scoredMarks = previousResponse?.scored_marks ?? 0;
  const cleanedQuestionText = removeRemarks(question_text);

  return (
    <div className={styles.questionCard}>
      {/* Question Header */}
      <div className={styles.questionHeader}>
        <h4 className={styles.questionTitle}>Question {index + 1}</h4>
        <div className={styles.questionMeta}>
          <span className={styles.questionType}>Question Type: {getQuestionTypeName()}</span>
          <span className={styles.questionMarks}>
            Marks: {marks}
            {negative_marks ? (
              <span className={styles.negative}>, Negative: {negative_marks % 1 === 0 ? Math.floor(negative_marks) : negative_marks}</span>
            ) : null}
          </span>
        </div>
      </div>

      {/* Question Content */}
      <div className={styles.questionContent}>
        {/* Question Text */}
        <div className={styles.questionText}>
          {cleanedQuestionText ? parse(cleanedQuestionText) : ''}
        </div>

        {/* Response/Options */}
        {renderQuestionContent()}
      </div>

      {/* Question Footer */}
      <div className={styles.questionFooter}>
        <button
          className={`${styles.answerBtn} ${showAnswer ? styles.active : ''}`}
          onClick={() => setShowAnswer(!showAnswer)}
        >
          {showAnswer ? 'Hide Answer' : 'Answer'}
        </button>

        {needsGradeButton() && isInstructor && !isGrading && !isPreviewMode && (
          <button className={styles.gradeBtn} onClick={handleOpenGrading}>
            Grade
          </button>
        )}

        {shouldShowMarks && !isPreviewMode && (
          <div className={styles.scoredMarks}>
            <span>Scored Marks</span>
            <input
              type="number"
              value={scoredMarks}
              disabled
              readOnly
            />
          </div>
        )}
      </div>

      {/* Grading Form */}
      {isGrading && (
        <div className={styles.gradingForm}>
          <div className={styles.gradingFormHeader}>
            <h6>Grade Question</h6>
            <button
              className={styles.gradingCloseBtn}
              onClick={handleCloseGrading}
              title="Close"
            >
              <FiX size={18} />
            </button>
          </div>
          <div className={styles.gradingFormBody}>
            <div className={styles.gradingFormField}>
              <label>Marks (out of {marks})</label>
              <input
                type="number"
                min="0"
                max={marks}
                step="0.1"
                value={gradingMarks}
                onChange={(e) => setGradingMarks(e.target.value)}
                placeholder="Enter marks"
              />
            </div>
            <div className={styles.gradingFormField}>
              <label>Remarks / Comments</label>
              <textarea
                rows={3}
                maxLength={255}
                value={gradingComments}
                onChange={(e) => setGradingComments(e.target.value)}
                placeholder="Enter remarks or comments (optional)"
              />
              <small style={{ color: gradingComments.length > 255 ? '#dc2626' : '#6b7280', fontSize: 12 }}>
                {gradingComments.length}/255 characters
              </small>
            </div>
            <div className={styles.gradingFormActions}>
              <button
                className={styles.gradingCancelBtn}
                onClick={handleCloseGrading}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                className={styles.gradingSaveBtn}
                onClick={handleGradeSave}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Answer Explanation */}
      {showAnswer && (
        <div className={styles.answerExplanation}>
          {/* Show correct answer for applicable question types */}
          {getCorrectAnswerText() && (
            <p className={styles.correctAnswerText}>
              <strong>Correct answer is:</strong> {getCorrectAnswerText()}
            </p>
          )}
          <h6>Answer Explanation:</h6>
          <div>
            {answer_explaination ? (
              parse(answer_explaination)
            ) : (
              <p>No explanation provided.</p>
            )}
          </div>
        </div>
      )}

      <Dialog
        open={Boolean(previewFile)}
        onClose={() => setPreviewFile(null)}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle className={styles.previewDialogTitle}>
          <span className={styles.previewDialogTitleText}>{previewFile?.name || ''}</span>
          <IconButton
            aria-label="Close preview"
            onClick={() => setPreviewFile(null)}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          className={styles.previewDialogContent}
          sx={{ overflow: 'hidden' }}
        >
          {previewFile && (
            <DocumentView
              selectedSubSection={{
                data: {
                  document_file: toRelativeFilePath(previewFile.url),
                  title: previewFile.name,
                  description: '',
                },
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuestionCardWrapper;
